import { getChartsSelections, getPlaylistDetailResolved } from "./api";
import { mapTrackToCard } from "./mappers";
import { FALLBACK_IMAGE_CARD, resolveUserAvatar, upscaleArtwork } from "../../models/music/common";

const DISCOVERY_CHART_LIMIT = 12;
const DISCOVERY_GROUP_LIMIT = 8;
const DISCOVERY_RELEASE_LIMIT = 16;

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeTrackCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function parseYear(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
}

function buildGenreChips(groups = []) {
  return groups.slice(0, DISCOVERY_GROUP_LIMIT).map((group, index) => {
    const firstPlaylist = toArray(group?.items?.collection)[0] || {};
    const trackCount = normalizeTrackCount(firstPlaylist?.track_count || firstPlaylist?.tracks?.length);
    const artwork =
      upscaleArtwork(firstPlaylist?.artwork_url, "t500x500") ||
      resolveUserAvatar(firstPlaylist?.user) ||
      FALLBACK_IMAGE_CARD;

    return {
      id: String(group?.id || group?.title || `genre-${index + 1}`),
      name: group?.title || "Unknown Chart",
      tracks: `${trackCount || 0} tracks`,
      image: artwork,
      source: firstPlaylist?.user?.username || "Music Charts",
    };
  });
}

function buildSelectionPlaylists(groups = []) {
  return groups.slice(0, DISCOVERY_GROUP_LIMIT).map((group) => ({
    id: group?.id || "",
    title: group?.title || "Unknown chart",
    playlists: toArray(group?.items?.collection)
      .slice(0, DISCOVERY_RELEASE_LIMIT)
      .map((playlist) => ({
        id: Number(playlist?.id) || 0,
        title: playlist?.title || "Untitled release",
        creator: playlist?.user?.username || "Unknown creator",
        year: parseYear(playlist?.last_modified),
        trackCount: normalizeTrackCount(playlist?.track_count || playlist?.tracks?.length),
        image:
          upscaleArtwork(playlist?.artwork_url, "t500x500") ||
          resolveUserAvatar(playlist?.user) ||
          FALLBACK_IMAGE_CARD,
      }))
      .filter((playlist) => playlist.id > 0),
  }));
}

export async function fetchDiscoveryFeed() {
  const charts = await getChartsSelections({ limit: DISCOVERY_CHART_LIMIT });
  const groups = toArray(charts?.collection);

  if (!groups.length) {
    throw new Error("No discovery chart data available.");
  }

  const genreChips = buildGenreChips(groups);
  const chartSelections = buildSelectionPlaylists(groups);
  const defaultSelectionId = chartSelections[0]?.id || "";
  const newReleases = chartSelections[0]?.playlists || [];

  return {
    genres: genreChips,
    chartSelections,
    defaultSelectionId,
    newReleases,
    recommendedCards: [],
  };
}

export async function fetchDiscoveryPlaylistTracks(playlistId) {
  const id = Number(playlistId);
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error("Invalid playlist id.");
  }

  const playlist = await getPlaylistDetailResolved(id);
  const tracks = toArray(playlist?.tracks).map(mapTrackToCard);

  return {
    playlist: {
      id,
      title: playlist?.title || "Untitled playlist",
      creator: playlist?.user?.username || "Unknown creator",
      year: parseYear(playlist?.last_modified),
      trackCount: normalizeTrackCount(playlist?.track_count || tracks.length),
    },
    tracks,
  };
}
